import React from 'react';
import {
  Badge,
  Box,
  Divider,
  Heading,
  List,
  ListIcon,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { OFFER, PLANS, capacityLabel, formatEuro } from '../config/plans';

const THEMES = {
  dark: {
    card: 'brand.quantumCard',
    border: 'brand.quantumBorder',
    heading: 'white',
    body: 'brand.quantumMuted',
    subtle: 'brand.quantumSubtle',
    accent: 'brand.quantumAccent',
    highlight: '0 0 0 1px rgba(0, 212, 170, 0.25), 0 28px 80px rgba(0, 212, 170, 0.16)',
    shadow: '0 18px 48px rgba(0, 0, 0, 0.28)',
  },
  light: {
    card: 'white',
    border: 'gray.200',
    heading: 'gray.800',
    body: 'gray.600',
    subtle: 'gray.500',
    accent: 'brand.teal',
    highlight: '0 0 0 3px rgba(13, 148, 136, 0.16)',
    shadow: 'sm',
  },
};

export const priceLines = (plan, cycle) => {
  if (!plan.price) {
    return {
      main: `From ${formatEuro(plan.licenseFromYearly)} / year`,
      sub: `Licence, plus onboarding from ${formatEuro(plan.onboardingFrom)}. Quoted after scoping.`,
    };
  }
  if (cycle === 'yearly') {
    return {
      main: `${formatEuro(plan.price.yearly)} / year`,
      sub: `Paid upfront: ten months for twelve. Or ${formatEuro(plan.price.monthly)} / month.`,
    };
  }
  return {
    main: `${formatEuro(plan.price.monthly)} / month`,
    sub: `Or ${formatEuro(plan.price.yearly)} / year paid upfront. No annual commitment on monthly.`,
  };
};

export const PlanCard = ({ planKey, cycle = 'monthly', variant = 'dark', highlighted = false, badge = null, actions = null }) => {
  const theme = THEMES[variant];
  const plan = PLANS[planKey];
  const price = priceLines(plan, cycle);
  const capacity = capacityLabel(plan);
  return (
    <Box
      data-testid={`plan-card-${planKey}`}
      bg={theme.card}
      borderRadius="2xl"
      p={6}
      borderWidth="1px"
      borderColor={highlighted ? theme.accent : theme.border}
      boxShadow={highlighted ? theme.highlight : theme.shadow}
      display="flex"
      flexDirection="column"
    >
      {badge ? <Badge alignSelf="flex-start" mb={3} colorScheme="teal">{badge}</Badge> : null}
      <Heading size="lg" color={theme.heading} mb={1}>{plan.name}</Heading>
      <Text fontSize="sm" color={theme.subtle} mb={4}>{plan.audience}</Text>
      <Text fontSize="2xl" fontWeight="800" color={theme.accent}>{price.main}</Text>
      <Text fontSize="xs" color={theme.subtle} mb={4}>{price.sub}</Text>
      {capacity ? <Text fontSize="sm" fontWeight="700" color={theme.heading} mb={3}>{capacity}</Text> : null}
      <Text color={theme.heading} fontWeight="700" mb={1}>{plan.job}</Text>
      <Text color={theme.body} fontSize="sm" mb={4}>{plan.result}</Text>
      <Divider mb={4} borderColor={theme.border} />
      <List spacing={2} mb={4}>
        {plan.features.map((feature) => (
          <ListItem key={feature} display="flex" alignItems="flex-start">
            <ListIcon as={CheckIcon} color={theme.accent} mt={1} />
            <Text color={theme.body} fontSize="sm">{feature}</Text>
          </ListItem>
        ))}
      </List>
      {planKey === 'business' ? (
        <Box borderWidth="1px" borderColor={theme.border} borderRadius="lg" p={3} mb={4}>
          <Text fontSize="sm" fontWeight="700" color={theme.heading}>
            20-person package: {formatEuro(plan.capacityPackage.price.monthly)} / month or {formatEuro(plan.capacityPackage.price.yearly)} / year
          </Text>
          <Text fontSize="xs" color={theme.body}>
            Same Business features with {plan.capacityPackage.included.seats} people and {plan.capacityPackage.included.customers.toLocaleString('en-US')} active clients. The checkout applies whichever option costs less for the capacity you ask for.
          </Text>
        </Box>
      ) : null}
      {plan.note ? <Text fontSize="xs" color={theme.subtle} mb={3}>{plan.note}</Text> : null}
      <Text fontSize="xs" color={theme.subtle} mb={4}>{plan.support}</Text>
      <VStack mt="auto" align="stretch" spacing={2}>{actions}</VStack>
    </Box>
  );
};

const extensionLine = `Extra person ${formatEuro(OFFER.addons.seat.price.monthly)}/month (${formatEuro(OFFER.addons.seat.price.yearly)}/year) · ${OFFER.addons.customerBlock.size.toLocaleString('en-US')} more active clients ${formatEuro(OFFER.addons.customerBlock.price.monthly)}/month (${formatEuro(OFFER.addons.customerBlock.price.yearly)}/year)`;

const Y = 'yes';
const N = 'no';
const COMPARISON = [
  ['Import with checks before saving, PDF/CSV reports', Y, Y, Y, Y],
  ['Review list, notes, actions, next contact and outcomes', Y, Y, Y, Y],
  ['Same methodology, evidence and data protection', Y, Y, Y, Y],
  ['Individual analysis with your own product catalogue', Y, Y, Y, Y],
  ['People included', '1', '3 (up to 10)', '5 (up to 20)', 'Contract'],
  ['Active clients included', '150', '1,000 (up to 5,000)', '2,500 (up to 10,000)', 'Contract'],
  ['Shared workspace, client assignment to advisers', N, Y, Y, Y],
  ['Lead sees each adviser’s queue, actions and outcomes', N, Y, Y, Y],
  ['Head, Compliance, Portfolio Adviser, View Only roles', N, N, Y, Y],
  ['Book-wide signals with EUR amounts on eligible positions', N, N, Y, Y],
  ['Audit console: search, activity, log export', N, N, Y, Y],
  ['Data exchange with your systems, company sign-in (SSO), own domain', N, N, N, 'Project'],
  ['Assisted onboarding (optional, one-off)', '—', '€290', '€590', 'From €10,000'],
];

const Cell = ({ value, theme }) => {
  if (value === Y) return <Text color={theme.accent} fontWeight="800" aria-label="Included">✓</Text>;
  if (value === N) return <Text color={theme.subtle} aria-label="Not included">—</Text>;
  return <Text color={theme.body} fontWeight="600" fontSize="sm">{value}</Text>;
};

export const PlanComparisonTable = ({ variant = 'dark' }) => {
  const theme = THEMES[variant];
  return (
    <Box overflowX="auto" bg={theme.card} borderWidth="1px" borderColor={theme.border} borderRadius="2xl" boxShadow={theme.shadow}>
      <Table variant="simple" minW="760px" size="sm">
        <Thead>
          <Tr>
            <Th color={theme.subtle} borderColor={theme.border}>What you get</Th>
            {['Solo', 'Team', 'Business', 'Enterprise'].map((name) => (
              <Th key={name} color={theme.subtle} borderColor={theme.border}>{name}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {COMPARISON.map(([label, ...values]) => (
            <Tr key={label}>
              <Td color={theme.heading} fontWeight="600" borderColor={theme.border}>{label}</Td>
              {values.map((value, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Td key={index} borderColor={theme.border}><Cell value={value} theme={theme} /></Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Text fontSize="xs" color={theme.subtle} px={4} py={3}>
        {extensionLine}. Prices exclude VAT; tax and total are shown at checkout. Higher plans do not unlock more accurate results: every plan shows what your data supports and what is missing.
      </Text>
    </Box>
  );
};

export default PlanCard;
